import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CatalystPortalAccountMatch,
  CatalystPortalContactMatch,
  CatalystPortalContactSyncResult,
  CatalystPortalProxyResult,
  CatalystPortalSettings,
  CatalystPortalSyncResult,
  CatalystPortalSyncSchedule,
  CatalystPortalTestResult,
  CatalystPortalTierMatch,
  CatalystPortalTierSyncResult,
  RunCatalystPortalContactSyncInput,
  RunCatalystPortalSyncInput,
  RunCatalystPortalTierSyncInput,
  UpdateCatalystPortalScheduleInput,
  UpdateCatalystPortalSettingsInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';

const portalKeys = {
  settings: ['catalyst-portal', 'settings'] as const,
  schedule: ['catalyst-portal', 'sync', 'schedule'] as const,
  candidates: (search: string) => ['catalyst-portal', 'sync', 'candidates', search] as const,
  tierSchedule: ['catalyst-portal', 'tier-sync', 'schedule'] as const,
  tierCandidates: (search: string) =>
    ['catalyst-portal', 'tier-sync', 'candidates', search] as const,
  contactSchedule: ['catalyst-portal', 'contact-sync', 'schedule'] as const,
  contactCandidates: (search: string) =>
    ['catalyst-portal', 'contact-sync', 'candidates', search] as const,
};

export function useCatalystPortalSettings(): UseQueryResult<CatalystPortalSettings> {
  return useQuery({
    queryKey: portalKeys.settings,
    queryFn: () => api.get<CatalystPortalSettings>('/catalyst-portal/settings'),
  });
}

export function useUpdateCatalystPortalSettings(): UseMutationResult<
  CatalystPortalSettings,
  unknown,
  UpdateCatalystPortalSettingsInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCatalystPortalSettingsInput) =>
      api.put<CatalystPortalSettings>('/catalyst-portal/settings', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portalKeys.settings });
    },
  });
}

export function useTestCatalystPortal(): UseMutationResult<
  CatalystPortalTestResult,
  unknown,
  void
> {
  return useMutation({
    mutationFn: () => api.post<CatalystPortalTestResult>('/catalyst-portal/settings/test'),
  });
}

export function useCatalystPortalProxy(): UseMutationResult<
  CatalystPortalProxyResult,
  unknown,
  string
> {
  return useMutation({
    mutationFn: (path: string) =>
      api.post<CatalystPortalProxyResult>('/catalyst-portal/proxy', { path }),
  });
}

export function useCatalystPortalSchedule(): UseQueryResult<CatalystPortalSyncSchedule> {
  return useQuery({
    queryKey: portalKeys.schedule,
    queryFn: () => api.get<CatalystPortalSyncSchedule>('/catalyst-portal/sync'),
  });
}

export function useUpdateCatalystPortalSchedule(): UseMutationResult<
  CatalystPortalSyncSchedule,
  unknown,
  UpdateCatalystPortalScheduleInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCatalystPortalScheduleInput) =>
      api.put<CatalystPortalSyncSchedule>('/catalyst-portal/sync', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portalKeys.schedule });
    },
  });
}

/** PSA customers with a NetSuite Account ID, paired with the portal account they resolve to. */
export function useCatalystPortalSyncCandidates(
  search: string,
  enabled: boolean,
): UseQueryResult<CatalystPortalAccountMatch[]> {
  return useQuery({
    queryKey: portalKeys.candidates(search),
    queryFn: () => {
      const params = new URLSearchParams({ limit: '100' });
      if (search.trim()) params.set('search', search.trim());
      return api.get<CatalystPortalAccountMatch[]>(
        `/catalyst-portal/sync/candidates?${params.toString()}`,
      );
    },
    enabled,
  });
}

export function useRunCatalystPortalSync(): UseMutationResult<
  CatalystPortalSyncResult,
  unknown,
  RunCatalystPortalSyncInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RunCatalystPortalSyncInput) =>
      api.post<CatalystPortalSyncResult>('/catalyst-portal/sync/run', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portalKeys.schedule });
      void queryClient.invalidateQueries({ queryKey: ['catalyst-portal', 'sync', 'candidates'] });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Caia tier -> customer type sync
// ---------------------------------------------------------------------------

export function useCatalystPortalTierSchedule(): UseQueryResult<CatalystPortalSyncSchedule> {
  return useQuery({
    queryKey: portalKeys.tierSchedule,
    queryFn: () => api.get<CatalystPortalSyncSchedule>('/catalyst-portal/tier-sync'),
  });
}

export function useUpdateCatalystPortalTierSchedule(): UseMutationResult<
  CatalystPortalSyncSchedule,
  unknown,
  UpdateCatalystPortalScheduleInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCatalystPortalScheduleInput) =>
      api.put<CatalystPortalSyncSchedule>('/catalyst-portal/tier-sync', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portalKeys.tierSchedule });
    },
  });
}

/** PSA customers with a NetSuite Account ID, paired with the tier the portal reports. */
export function useCatalystPortalTierCandidates(
  search: string,
  enabled: boolean,
): UseQueryResult<CatalystPortalTierMatch[]> {
  return useQuery({
    queryKey: portalKeys.tierCandidates(search),
    queryFn: () => {
      const params = new URLSearchParams({ limit: '100' });
      if (search.trim()) params.set('search', search.trim());
      return api.get<CatalystPortalTierMatch[]>(
        `/catalyst-portal/tier-sync/candidates?${params.toString()}`,
      );
    },
    enabled,
  });
}

export function useRunCatalystPortalTierSync(): UseMutationResult<
  CatalystPortalTierSyncResult,
  unknown,
  RunCatalystPortalTierSyncInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RunCatalystPortalTierSyncInput) =>
      api.post<CatalystPortalTierSyncResult>('/catalyst-portal/tier-sync/run', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portalKeys.tierSchedule });
      void queryClient.invalidateQueries({
        queryKey: ['catalyst-portal', 'tier-sync', 'candidates'],
      });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Portal user -> PSA contact mirror
// ---------------------------------------------------------------------------

export function useCatalystPortalContactSchedule(): UseQueryResult<CatalystPortalSyncSchedule> {
  return useQuery({
    queryKey: portalKeys.contactSchedule,
    queryFn: () => api.get<CatalystPortalSyncSchedule>('/catalyst-portal/contact-sync'),
  });
}

export function useUpdateCatalystPortalContactSchedule(): UseMutationResult<
  CatalystPortalSyncSchedule,
  unknown,
  UpdateCatalystPortalScheduleInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCatalystPortalScheduleInput) =>
      api.put<CatalystPortalSyncSchedule>('/catalyst-portal/contact-sync', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portalKeys.contactSchedule });
    },
  });
}

/** PSA contacts paired with the portal user each one resolves to. */
export function useCatalystPortalContactCandidates(
  search: string,
  enabled: boolean,
): UseQueryResult<CatalystPortalContactMatch[]> {
  return useQuery({
    queryKey: portalKeys.contactCandidates(search),
    queryFn: () => {
      const params = new URLSearchParams({ limit: '100' });
      if (search.trim()) params.set('search', search.trim());
      return api.get<CatalystPortalContactMatch[]>(
        `/catalyst-portal/contact-sync/candidates?${params.toString()}`,
      );
    },
    enabled,
  });
}

export function useRunCatalystPortalContactSync(): UseMutationResult<
  CatalystPortalContactSyncResult,
  unknown,
  RunCatalystPortalContactSyncInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RunCatalystPortalContactSyncInput) =>
      api.post<CatalystPortalContactSyncResult>('/catalyst-portal/contact-sync/run', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: portalKeys.contactSchedule });
      void queryClient.invalidateQueries({
        queryKey: ['catalyst-portal', 'contact-sync', 'candidates'],
      });
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}
